import { Button } from "@outschool/backpack";
import { CreditAccountStatus } from "@outschool/gql-backend-generated";
import {
  SendTestEmailMutation,
  SendTestEmailMutationVariables,
} from "@outschool/gql-frontend-generated";
import {
  Trans,
  renderCurrencyString,
  useTranslation,
} from "@outschool/localization";
import { useMutation } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { useTopNoticeContext } from "@outschool/ui-components-website";
import React from "react";

import {
  getEmailToConfirmToUseCredit,
  hasUnconfirmedEmailForPricingOffer,
} from "../../shared/User";
import {
  usePricingOffers,
  useResendEmailConfirmation,
} from "../hooks/CurrentUser";
import { useCreditAccountCached } from "../queries/CreditQueries";
import EmailBanner from "./banners/EmailBanner";
import EsaBanner from "./banners/EsaBanner";
import EsaMembershipBanner from "./banners/EsaMembershipBanner";
import FloridaEMABanner from "./banners/FloridaEMABanner";
import { sendTestEmailMutation } from "./emailWarningQueries";

export default function AccountBanner() {
  const {
    currentUser,
    currentUserHasLoaded,
    isLoggedIn,
    isEsaSession,
    destroyEsaSession,
    currentUserRefetch,
  } = useSession();
  const pricingOffers = usePricingOffers();
  const { setTimedTopNotice } = useTopNoticeContext();
  const { creditAccount, loading: creditAccountLoading } =
    useCreditAccountCached(false);
  const { t } = useTranslation("client\\components\\AccountBanner");
  const hasAnUnconfirmedEmailForPricingOffer =
    hasUnconfirmedEmailForPricingOffer(currentUser, currentUser?.pricingOffer);
  const FLORDIA_RESTRICTED_WALLETS_ORG_UID =
    "01f11fa5-f9a9-441d-96dc-12a6e62b08af";

  const emailToConfirm = getEmailToConfirmToUseCredit(
    currentUser,
    currentUser?.pricingOffer,
  );
  const resendEmail = useResendEmailConfirmation(currentUser, emailToConfirm);
  const [sendTestEmail, { loading: sendTestEmailLoading }] = useMutation<
    SendTestEmailMutation,
    SendTestEmailMutationVariables
  >(sendTestEmailMutation);

  const shouldShowEmail =
    currentUserHasLoaded &&
    !creditAccountLoading &&
    isLoggedIn &&
    currentUser &&
    emailToConfirm !== null;

  const hasOsCash =
    creditAccount &&
    creditAccount.balance > 0 &&
    creditAccount.status === CreditAccountStatus.RequiresConfirmedPrimaryEmail;

  if (!currentUser) {
    return null;
  }

  const currentUserEmail = currentUser.email;

  const emailBouncedBanner = (
    <EmailBanner>
      <Trans t={t}>
        {{ currentUserEmail }} is rejecting some emails from Outschool. Please
        check your email settings and{" "}
        <Button
          variant="link"
          color="primary"
          sx={{
            padding: 0,
            minHeight: "auto",
            height: "auto",
            fontSize: "1em",
            lineHeight: "inherit",
            fontWeight: "inherit",
            verticalAlign: "baseline",
            textDecoration: "underline",
            textUnderlineOffset: "0.1em",
          }}
          disabled={sendTestEmailLoading}
          onClick={async () => {
            await sendTestEmail();
            setTimedTopNotice(
              t("Sent a test email to {{currentUserEmail}}.", {
                currentUserEmail,
              }),
            );
          }}
        >
          click here
        </Button>{" "}
        to receive a test email.
      </Trans>
    </EmailBanner>
  );

  let message = t(
    "Please confirm your email address by clicking the link we sent to {{emailToConfirm}}",
    { emailToConfirm },
  );
  if (
    currentUser?.pricingOffer &&
    hasAnUnconfirmedEmailForPricingOffer &&
    !currentUser?.pricingOffer?.isFinancialAid
  ) {
    const buyerOrgName = currentUser.pricingOffer?.buyerOrgName;
    message = t(
      "To use {{buyerOrgName}} funds, please click on the confirmation link we emailed to {{emailToConfirm}}.",
      { buyerOrgName, emailToConfirm },
    );
  } else if (hasOsCash && currentUser?.hasSubscription) {
    message = t(
      "To use your Outschool Balance, please confirm your email address by clicking the link we sent to {{emailToConfirm}}. ",
      { emailToConfirm },
    );
  } else if (hasOsCash) {
    const currencyString = renderCurrencyString({
      priceInCents: creditAccount.balance,
      showCurrencyCode: true,
    });
    message = t(
      "To use your {{currencyString}} in Outschool Credits, please confirm your email address by clicking the link we sent to {{emailToConfirm}}. ",
      { currencyString, emailToConfirm },
    );
  }

  const emailConfirmationBanner = (
    <EmailBanner
      showResendButton
      onResendButtonClick={async () => {
        await resendEmail();
        setTimedTopNotice(
          t("A new confirmation email was sent to {{emailToConfirm}}.", {
            emailToConfirm,
          }),
        );
      }}
    >
      {message}
    </EmailBanner>
  );

  const hasRestrictedPricingOffer = pricingOffers?.some(
    po => po.organizationUid === FLORDIA_RESTRICTED_WALLETS_ORG_UID,
  );

  const esaMembershipBanner = (
    <EsaMembershipBanner
      bannerClosedAt={currentUser?.details?.bannerClosedAt}
    />
  );

  const classWalletBanner = (
    <EsaBanner
      onExitSession={() => {
        destroyEsaSession();
        if (currentUser?.hasSubscription) {
          currentUserRefetch();
        }
      }}
    />
  );

  const pickEmailBanner = () => {
    if (currentUser.email_bounced_at) {
      return emailBouncedBanner;
    } else if (shouldShowEmail) {
      return emailConfirmationBanner;
    } else {
      return null;
    }
  };

  const pickEsaBanner = () => {
    if (hasRestrictedPricingOffer) {
      return <FloridaEMABanner />;
    } else if (currentUser?.hasEsaMembership) {
      return esaMembershipBanner;
    } else if (isEsaSession) {
      return classWalletBanner;
    } else {
      return null;
    }
  };

  return (
    <>
      {pickEmailBanner()}
      {pickEsaBanner()}
    </>
  );
}
